import { FontWeight, Heading, LiliButton, LiliButtonType, Text } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import logoUrl from '@Assets/logos/lili-logo-blue.svg';
import { ReactComponent as DocumentIllustration } from '@Assets/banners/document-illustration.svg';
import type { DocumentRequest } from '../logic/useVerificationCenterHook';
import type { BUSINESS_FILE_TYPES } from '@Store/slices/kyb/kyb.types';

interface IIntroScreenProps {
  onContinue: () => void;
  requiredDocuments: DocumentRequest[];
}

interface IntroDocumentItem {
  key: string;
  label: string;
}

export const IntroScreen = ({ onContinue, requiredDocuments }: IIntroScreenProps) => {
  const { t } = useTranslation();

  const getDocumentIntroName = (fileType: keyof typeof BUSINESS_FILE_TYPES): string => {
    const introKey = `pages.verificationCenter.documents.${fileType}.intro` as const;
    return t(introKey);
  };

  const getIntroDocuments = () => {
    const seenLlcDocTypes = new Set<string>();
    return requiredDocuments
      .map((doc): IntroDocumentItem | null => {
        if (doc.type === 'NON_US_UBO') {
          return {
            key: `${doc.fileType}-${doc.externalId || doc.uboName}`,
            label: `${getDocumentIntroName(doc.fileType)} ${t('pages.verificationCenter.intro.uboLabel')}: ${doc.uboName}`,
          };
        }
        if (!seenLlcDocTypes.has(doc.fileType)) {
          seenLlcDocTypes.add(doc.fileType);
          return {
            key: doc.fileType,
            label: getDocumentIntroName(doc.fileType),
          };
        }
        return null;
      })
      .filter((doc) => doc !== null);
  };

  const introDocuments = getIntroDocuments();

  return (
    <div className='w-full h-full flex flex-col sm:flex-row bg-surface-primary-bw-00 overflow-auto'>
      <div className='flex flex-col justify-between w-full sm:w-1/2 h-full'>
        <div className='flex flex-col items-center px-[12px] sm:px-[24px] sm:pt-[48px] overflow-auto'>
          <div className='flex flex-col w-full max-w-[400px]'>
            <div className='flex flex-col gap-[16px]'>
              <div className='flex flex-col'>
                <img
                  src={logoUrl}
                  alt='Lili logo'
                  className='mx-auto sm:mx-0 w-[40px] sm:w-[64px] pb-[8px] pt-[8px] sm:pt-0 sm:pb-0'
                />
                <span className='w-full h-[1px] border-b border-b-surface-primary-bw-03 mb-[12px] sm:hidden' />
              </div>

              <div className='flex flex-col gap-[8px]'>
                <Heading.H5 fontWeight={FontWeight.SEMIBOLD}>
                  {t('pages.verificationCenter.intro.title')}
                </Heading.H5>
                <Text.H9>{t('pages.verificationCenter.intro.description')}</Text.H9>
              </div>

              <ul className='flex flex-col gap-[2px] pl-[20px] marker:text-text-main-secondary'>
                {introDocuments.map((doc) => (
                  <li key={doc.key} className='list-disc'>
                    <Text.H9 className='text-text-main-secondary'>{doc.label}</Text.H9>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className='flex w-full bg-surface-primary-bw-00 sticky bottom-0 left-0 py-[16px]'>
          <div className='flex flex-col gap-[16px] items-center w-full px-[12px] sm:px-[24px]'>
            <div className='flex w-full max-w-[375px]'>
              <LiliButton
                onClick={onContinue}
                id='verification-center-intro__continue-button'
                type={LiliButtonType.PRIMARY}
                title={t('pages.verificationCenter.intro.continueButton')}
                className='w-full'
              />
            </div>
          </div>
        </div>
      </div>

      <div className='hidden sm:flex w-1/2 bg-surface-primary-bw-02 border-l border-outline-primary-bw-03 items-center justify-end'>
        <DocumentIllustration />
      </div>
    </div>
  );
};
